import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Box, Button } from '@mui/material';

import { getScroll } from '../api';
import {
  ProductGrid,
  Header,
  SearchError,
  LoadingCircle
} from '../components';
import getApiErrorMessage from '../helpers/apiErrorMessage';


export default function SearchResults({ auth }) {
  const [data, setData] = useState(null);
  const [scroll, setScroll] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadScroll, setLoadScroll] = useState(false);
  const [hasScrolled, setHasScrolled] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [loadMoreCancelled, setLoadMoreCancelled] = useState(false);
  const [loadMoreError, setLoadMoreError] = useState(null);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [prevPage, setPrevPage] = useState(0);
  const requestInFlight = useRef(false);
  const loadMoreController = useRef(null);
  const loadMoreCancelRequested = useRef(false);
  const loadMoreRef = useRef(null);

  const location = useLocation();
  const navigate = useNavigate();

  const cancelLoadMore = useCallback(() => {
    if (!loadMoreController.current) return;
    loadMoreCancelRequested.current = true;
    loadMoreController.current.abort();
  }, []);

  const retryLoadMore = () => {
    setLoadMoreError(null);
    setLoadMoreCancelled(false);
    setPage(currentPage => currentPage + 1);
  };

  useEffect(() => {
    if (loading && loadMoreController.current) {
      loadMoreCancelRequested.current = true;
      loadMoreController.current.abort();
    }
  }, [loading]);

  useEffect(() => {
    if (!loadScroll) return undefined;

    const handleKeyDown = event => {
      if (event.key === 'Escape') cancelLoadMore();
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [cancelLoadMore, loadScroll]);


  useEffect(() => {
    if (location.state?.skus) {
      setData(location.state.skus);
      setHasMore(Boolean(location.state.scroll));
      setScroll(location.state.scroll ?? null);
      setHasScrolled(false);
      setLoadMoreCancelled(false);
      setLoadMoreError(null);
      setError(null);
      setPage(0);
      setPrevPage(0);
    }
    else  {
      navigate("/");
    }
  }, [location.state, navigate]);


  useEffect(() => {
    if (
      !hasScrolled ||
      requestInFlight.current ||
      !scroll ||
      !hasMore ||
      loading ||
      error ||
      loadMoreCancelled
    ) return;

    if (page === prevPage) return;

    let cancelled = false;
    const controller = new AbortController();
    loadMoreController.current = controller;
    loadMoreCancelRequested.current = false;
    setLoadMoreCancelled(false);
    requestInFlight.current = true;
    setLoadScroll(true);
    const fetchScroll = async () => {
      try {
        const response = await getScroll(scroll, controller.signal);
        const nextSkus = response?.skus ?? [];
        if (!cancelled) {
          if (nextSkus.length > 0) {
            setData(prev => [...prev, ...nextSkus]);
          }
          setHasMore(nextSkus.length > 0);
          setPrevPage(page);
        }
      } catch (error) {
        if (!cancelled) {
          if (error.kind === 'cancelled') {
            setLoadMoreCancelled(true);
          } else if (error.kind !== 'cancelled') {
            setLoadMoreCancelled(true);
            setLoadMoreError(getApiErrorMessage(error));
          }
        }
      } finally {
        if (loadMoreController.current === controller) {
          loadMoreController.current = null;
          requestInFlight.current = false;
          setLoadScroll(false);
        }
        if (loadMoreCancelRequested.current && !loading) {
          setLoadMoreCancelled(true);
        }
      }
    };

    fetchScroll();
    return () => {
      cancelled = true;
    };
  }, [page, hasScrolled, scroll, prevPage, hasMore, loading, error, loadMoreCancelled]);


  useEffect(() => {
    const loadMoreElement = loadMoreRef.current;
    if (!loadMoreElement || !scroll || !hasMore || loading || loadScroll || error || loadMoreCancelled) {
      return undefined;
    }

    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting && !requestInFlight.current) {
        setHasScrolled(true);
        setPage(prev => prev + 1);
      }
    }, { rootMargin: '300px' });

    observer.observe(loadMoreElement);
    return () => observer.disconnect();
  }, [scroll, hasMore, loading, loadScroll, error, loadMoreCancelled]);


  return (
    <Box sx={{
      "display": "flex",
      "flexDirection": "column",
      "alignItems": "center",
      "justifyContent": "center",
      "marginY": "40px",
      "marginX": { xs: 2, md: 10 },
      // backgroundColor: "blue"
    }}>
      <Header
        token={auth.token}
        setLoading={setLoading}
        setError={setError}
      />

      {error && !loading? <SearchError message={error} /> : null}
      {loadMoreError ? <SearchError message={loadMoreError} /> : null}

      {loading && !loadScroll ? <LoadingCircle /> : null}

      {(!error && !loading)? <ProductGrid skus={data} /> : null}

      {loadScroll ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <LoadingCircle />
          <Button size="small" onClick={cancelLoadMore}>Cancelar carregamento (Esc)</Button>
        </Box>
      ) : null}
      {!loading && !loadScroll && loadMoreCancelled && hasMore ? (
        <Button size="small" onClick={retryLoadMore}>Tentar carregar novamente</Button>
      ) : null}
      {hasMore && scroll ? <Box ref={loadMoreRef} sx={{ height: 1, width: '100%' }} /> : null}

    </Box>
  );
}
